import {useTranslation} from "react-i18next";
import {useEffect, useRef} from "react";
import {ForkOutlined} from "@ant-design/icons";
import {Button, Divider, Form, Input, InputNumber, Select, Switch, Typography} from "antd";
import ApiService from "../../core/service/api-service";
import ModalitySelectBox from "../modality/ModalitySelectBox";
import FirmSelectBox from "../firm/FirmSelectBox";
import {useHistory} from "react-router-dom";
import {Pages} from "../../AppRouter";
import StudyTypeEnum from "../../enums/StudyTypeEnum";

// Görüntü açma kilidi varsayılan süreleri (dk) — sunucudaki HospitalHelper.DefaultStudyOpenLockMinutes ile aynı.
const defaultStudyOpenLockMinutes = {
    ROUTINE: 120,
    INPATIENT: 120,
    EMERGENCY: 20,
    COMPARATIVE_EXAMINATION: 120,
}

const timezoneOptions = [
    { label: 'UTC-12:00', value: -720 },
    { label: 'UTC-11:00', value: -660 },
    { label: 'UTC-10:00', value: -600 },
    { label: 'UTC-09:00', value: -540 },
    { label: 'UTC-08:00', value: -480 },
    { label: 'UTC-07:00', value: -420 },
    { label: 'UTC-06:00', value: -360 },
    { label: 'UTC-05:00', value: -300 },
    { label: 'UTC-04:00', value: -240 },
    { label: 'UTC-03:00', value: -180 },
    { label: 'UTC-02:00', value: -120 },
    { label: 'UTC-01:00', value: -60 },
    { label: 'UTC+00:00', value: 0 },
    { label: 'UTC+01:00', value: 60 },
    { label: 'UTC+02:00', value: 120 },
    { label: 'UTC+03:00 (Türkiye)', value: 180 },
    { label: 'UTC+04:00', value: 240 },
    { label: 'UTC+05:00', value: 300 },
    { label: 'UTC+05:30', value: 330 },
    { label: 'UTC+06:00', value: 360 },
    { label: 'UTC+07:00', value: 420 },
    { label: 'UTC+08:00', value: 480 },
    { label: 'UTC+09:00', value: 540 },
    { label: 'UTC+10:00', value: 600 },
    { label: 'UTC+11:00', value: 660 },
    { label: 'UTC+12:00', value: 720 },
];

export default function HospitalCreateEdit({hospitalId, onSaved})
{
    const {t} = useTranslation();
    const history = useHistory();
    const formRef = useRef();

    const getHospital = async () => {
        if(hospitalId)
        {
            const response = await ApiService.Hospital.Get(hospitalId);
            if(response.success)
            {
                let mins = {}
                Object.keys(StudyTypeEnum).forEach((key, idx) => {
                    const data = response.data.studyTypesMin[StudyTypeEnum[key]-1]
                    let min = '1440'
                    let timeout = '1440'

                    if(data) {
                        min = data.split(':')[0]
                        timeout = data.split(':')[1]
                    }
                    mins[`number_${key}_min`] = min
                    mins[`number_${key}_timeout`] = timeout
                    mins[`number_${key}_lock`] = response.data.studyOpenLockMinutes?.[StudyTypeEnum[key]-1] || defaultStudyOpenLockMinutes[key]
                })

                formRef.current.setFieldsValue({
                    firmId: response.data.firm.id,
                    name: response.data.name,
                    modalityIdList: response.data.modalityIdList,
                    officialName: response.data.officialName,
                    timezoneOffset: response.data.timezoneOffset ?? 180,
                    studyLock: response.data.studyLock,
                    modalityMapping: response.data.modalityMapping,
                    manualRequestEnabled: response.data.manualRequestEnabled !== false,
                    studyOpenLockEnabled: response.data.studyOpenLockEnabled === true,
                    ...mins
                })
            }
        }
    }

    const save = async (form) => {
        let studyTypesMin = [];
        Object.keys(StudyTypeEnum).forEach(key => {
            const min = form[`number_${key}_min`]
            const timeout = form[`number_${key}_timeout`]
            studyTypesMin.push(`${min}:${timeout}`)
        })
        form.studyTypesMin = studyTypesMin

        // Sunucu diziyi çekim tipi değerine göre (değer-1) okuyor.
        let studyOpenLockMinutes = [];
        Object.keys(StudyTypeEnum).forEach(key => {
            studyOpenLockMinutes[StudyTypeEnum[key] - 1] = form[`number_${key}_lock`]
        })
        form.studyOpenLockMinutes = studyOpenLockMinutes

        if(hospitalId)
        {
            const response = await ApiService.Hospital.Edit(hospitalId, form)
            if(response.success && onSaved)
                onSaved(hospitalId)
        }
        else
        {
            const response = await ApiService.Hospital.Create(form);
            if(response.success)
            {
                if(onSaved)
                    onSaved(response.data)
                else
                    history.push(Pages.Hospital.Edit(response.data))
            }
        }
    }

    useEffect(() => {
        getHospital()
    }, [])

    return (
        <>
            <Form
                layout={'vertical'}
                ref={formRef}
                onFinish={save}
            >

                <Form.Item
                    label={t('Firm')}
                    name={'firmId'}
                    rules={[
                        {required: true}
                    ]}
                >
                    <FirmSelectBox />
                </Form.Item>

                <Form.Item
                    name={'name'}
                    label={t('Name')}
                    rules={[
                        {required: true}
                    ]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name={'officialName'}
                    label={t('Official Name')}
                    rules={[
                        {required: true}
                    ]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name={'timezoneOffset'}
                    label={t('Timezone')}
                    initialValue={180}
                    rules={[
                        {required: true}
                    ]}
                >
                    <Select 
                        options={timezoneOptions}
                        showSearch
                        optionFilterProp="label"
                        style={{width: '100%'}}
                    />
                </Form.Item>

                <Form.Item
                    name={'studyLock'}
                    label={t('Study Lock')}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name={'modalityMapping'}
                    label={t('Modality Mapping')}
                    tooltip={t('Maps source modality to target modality')}
                >
                    <Input placeholder="DX:CR,US:MR" />
                </Form.Item>

                <Form.Item
                    name={'manualRequestEnabled'}
                    label={t('Allow Manual Request Adding')}
                    valuePropName={'checked'}
                    initialValue={true}
                    tooltip={t('When off, requests cannot be added manually to this hospital; only users with the restricted hospital permission can. HL7 requests are not affected.')}
                >
                    <Switch />
                </Form.Item>

                <Form.Item
                    name={'studyOpenLockEnabled'}
                    label={t('Study Open Lock')}
                    valuePropName={'checked'}
                    initialValue={false}
                    tooltip={t('Study open lock tooltip')}
                >
                    <Switch />
                </Form.Item>

                <Divider orientation={'left'} style={{marginTop: 24}}>
                    {t('Study Type Durations')}
                </Divider>

                <Typography.Paragraph type={'secondary'} style={{marginBottom: 16}}>
                    {t('Study type durations description')}
                </Typography.Paragraph>

                {
                    Object.keys(StudyTypeEnum).map(key => {

                        return (
                            <div
                                key={key}
                                style={{display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 4}}
                            >
                                <div style={{width: 170, paddingTop: 5, flexShrink: 0}}>
                                    <b>{t(key)}</b>
                                </div>
                                <Form.Item
                                    name={`number_${key}_min`}
                                    label={t('Report Duration')}
                                    tooltip={t('Report duration tooltip')}
                                    initialValue={1440}
                                    style={{flex: 1, marginBottom: 12}}
                                    rules={[
                                        {required: true}
                                    ]}
                                >
                                    <InputNumber
                                        min={1}
                                        addonAfter={t('minute short')}
                                        style={{width: '100%'}} />
                                </Form.Item>
                                <Form.Item
                                    name={`number_${key}_timeout`}
                                    label={t('Study Type Change Window')}
                                    tooltip={t('Study type change window tooltip')}
                                    initialValue={1440}
                                    style={{flex: 1, marginBottom: 12}}
                                    rules={[
                                        {required: true}
                                    ]}
                                >
                                    <InputNumber
                                        min={1}
                                        style={{width: '100%'}} />
                                </Form.Item>
                                <Form.Item
                                    name={`number_${key}_lock`}
                                    label={t('Study Open Lock Duration')}
                                    tooltip={t('Study open lock duration tooltip')}
                                    initialValue={defaultStudyOpenLockMinutes[key]}
                                    style={{flex: 1, marginBottom: 12}}
                                    rules={[
                                        {required: true}
                                    ]}
                                >
                                    <InputNumber
                                        min={1}
                                        addonAfter={t('minute short')}
                                        style={{width: '100%'}} />
                                </Form.Item>
                            </div>
                        )
                    })
                }

                <Form.Item
                    label={t('Modalities')}
                    name={'modalityIdList'}
                    rules={[{required: true}]}
                >
                    <ModalitySelectBox />
                </Form.Item>

                <Form.Item>
                    <Button type="primary" htmlType="submit">
                        {t('Save')}
                    </Button>
                </Form.Item>
            </Form>
        </>
    )
}
